// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const SocialProviderButton = require('client-v2/common/components/social-provider-button')
const socialProviders = require('client-v2/common/containers/auth-modal/providers')
const { ModalDepthOverlay } = require('client-v2/common/contexts/modal-depth')
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('AuthModal')

class AuthModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.contentRef = React.createRef()

    // Ensure function references do not change between render calls
    this.onHasAccountToggle = this.onHasAccountToggle.bind(this)
    this.onSocialProviderClick = this.onSocialProviderClick.bind(this)
  }

  // Event handler methods

  onClose() {
    this.props.onClose()
  }

  onHasAccountToggle() {
    const toggled = !this.props.hasAccount
    this.props.onHasAccountToggle(toggled)
  }

  onSocialProviderClick(event) {
    const { provider } = event.currentTarget.dataset
    this.props.onSocialProviderAuth(provider)
  }

  // Render methods

  render() {
    return (
      tag('div', {
        className: classNames(
          this.props.className,
          bem.block()
        )
      },
        this.renderBackdrop(),
        this.renderContent(),
        this.renderCloseButton()
      )
    )
  }

  renderBackdrop() {
    return (
      tag('div', {
        className: bem.element('backdrop'),
        onClick: this.props.onClose
      })
    )
  }

  renderContent() {
    return (
      tag('div', {
        ref: this.contentRef,
        className: bem.element('content')
      },
        tag(ModalDepthOverlay, {
          scrollableRef: this.contentRef
        }),
        tag('div', {
          className: bem.element('stack')
        },
          this.renderHeadings(),
          this.renderErrorMessage(),
          this.renderSocialButtons(),
          this.renderTermsOfService(),
          this.renderHasAccountToggle()
        )
      )
    )
  }

  renderHeadings() {
    if (this.props.errorType) {
      return null
    }
    const [ heading1, heading2 ] = this.props.hasAccount
    ? [ 'Welcome back to Mix!', 'Sign in below:' ]
    : [ 'Let’s get you set up', 'Select one to create your Mix account.' ]
    return tag([
      tag('div', { className: bem.element('heading-1') }, heading1),
      tag('div', { className: bem.element('heading-2') }, heading2)
    ])
  }

  renderErrorMessage() {
    const { errorType, errorProvider } = this.props
    if (!errorType) {
      return null
    }
    const providerName = socialProviders.getName(errorProvider)
    let message = 'Please try again.'
    if (errorType === 'permission') {
      message = 'Please make sure you are logged-in to your social account, then try again.'
    }
    return tag('div', {
      className: bem.element('error')
    },
      tag('div', {
        className: bem.element('error-title')
      },
        `There was a problem connecting with your ${providerName} account`
      ),
      tag('div', {
        className: bem.element('error-message')
      },
        message,
        '\n',
        'If you continue to have this problem, ',
        tag('a', {
          className: bem.element('error-href'),
          href: 'mailto:support@mix.com'
        },
          'contact support'
        ),
        '.'
      )
    )
  }

  renderCloseButton() {
    return (
      tag('button', {
        className: bem.element('close-button'),
        onClick: this.props.onClose
      })
    )
  }

  renderSocialButtons() {
    return tag('div', {
      className: bem.element('buttons-container')
    },
      this.props.socialProviders.map(provider => {
        if (
          // Hide certain providers based on login or signup prompt
          provider.hasAccount != null &&
          provider.hasAccount !== this.props.hasAccount
        ) {
          return null
        }
        return tag(SocialProviderButton, {
          key: provider.id,
          className: bem.element('button'),
          providerId: provider.id,
          providerName: provider.name,
          onClick: this.onSocialProviderClick
        })
      })
    )
  }

  renderTermsOfService() {
    if (this.props.hasAccount) {
      return null
    }
    return (
      tag('div', {
        className: bem.element('bottom-text')
      },
        `By continuing you agree to Mix's `,
        tag('a', {
          className: bem.element('bottom-href'),
          href: '/tos'
        },
          `Terms of Service`
        ),
        `, `,
        tag('a', {
          className: bem.element('bottom-href'),
          href: '/privacy'
        },
          `Privacy Policy`
        ),
        `.`
      )
    )
  }

  renderHasAccountToggle() {
    const { hasAccount } = this.props
    return (
      tag('div', {
        className: bem.element('bottom-text', {
          'sign-in': hasAccount,
          'sign-up': !hasAccount
        })
      },
        (hasAccount
          ? `Don't have a Mix account?`
          : `Already have a Mix account?`
        ),
        ` `,
        tag('a', {
          className: bem.element('bottom-href'),
          onClick: this.onHasAccountToggle
        },
          (hasAccount
            ? 'Sign up'
            : 'Sign in'
          )
        ),
        `.`
      )
    )
  }
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  AuthModal.propTypes = {
    // Flags
    hasAccount: PropTypes.bool,
    // Values
    className: PropTypes.string,
    socialProviders: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      hasAccount: PropTypes.bool
    })).isRequired,
    errorType: PropTypes.string,
    errorProvider: PropTypes.string,
    // Callbacks
    onClose: PropTypes.func.isRequired,
    onHasAccountToggle: PropTypes.func.isRequired,
    onSocialProviderAuth: PropTypes.func.isRequired
  }
}

module.exports = AuthModal
